import { Component, OnInit, ChangeDetectionStrategy, Input, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ExtractsService } from '../services/ExtractsService';
import { Subject, Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { merge } from 'rxjs/observable/merge';
import { of as observableOf } from 'rxjs/observable/of';
import { catchError } from 'rxjs/operators/catchError';
import { map } from 'rxjs/operators/map';
import { startWith } from 'rxjs/operators/startWith';
import { switchMap } from 'rxjs/operators/switchMap';
import { CurrentUserService } from '../../security/services/CurrentUserService';
import { BaseAddressInterceptor } from '../../core/interceptors/BaseAddressInterceptor';
import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { StoreDataSource } from '../../core/services/StoreDataSource';
import { ExtractModel } from "../models/ExtractModel";
import { ExtractListModel } from "../models/ExtractListModel";
import { OptionModel } from "../models/OptionModel";
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { SearchComponent } from '../../pillar/search/components/search.component';
import * as fromStore from '../store/reducers';
import * as extractsActions from '../store/actions';
import * as pillarActions from '../../pillar/dashboard/store/actions';
import { ModuleConfiguration } from '../extracts.module.config';
import { Actions } from '@ngrx/effects';
import { ACTION_ERROR, ActionError } from '../../pillar/common/actions/ActionError';
import { ExtractPageViewModel } from "../models/ExtractPageViewModel";

@Component({
  selector: 'extract-viewer',
  templateUrl: './extract-viewer.component.html',
  styleUrls: ['./extract-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExtractViewerComponent implements OnInit, OnDestroy {
  displayedExtractColumns: string[];
  selectedExtract: ExtractModel;
  extractListLoading$: Observable<boolean>;
  extractList$: Observable<ExtractListModel[]>;
  extractCategorySelected$: Observable<ExtractModel>;
  dataSource: StoreDataSource<ExtractListModel>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  isReadyToGenerate: boolean = true;
  searchInput = 'ExtractsModule_SearchInput';
  selectedExtractList_ExtractListModel = 'ExtractsModule_SelectedExtractListType';
  selectedExtractCategory_ExtractModel = 'ExtractsModule_SelectedExtractModel';
  editExtractsPermission = 'ExtractsModule.Extracts:Edit';

  constructor(private store: Store<any>,
    private router: Router,
    private actions$: Actions,
    private curentUserService: CurrentUserService,
    private baseAddressInterceptor: BaseAddressInterceptor,
    private _changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  @ViewChild(MatSort)
  sort: MatSort;
  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;
  @ViewChild(SearchComponent)
  search: SearchComponent;

  @Input()
  set extract(extract: ExtractModel) {
    this.selectedExtract = extract;
    this.displayedExtractColumns = this.selectedExtract.displayedExtractColumns;
  }
  get extract(): ExtractModel {
    return this.selectedExtract;
  }

  ngOnInit() {
    this.checkSelected();
    this.dataSource = new StoreDataSource<ExtractListModel>(
      this.destroyed$,
      this.paginator,
      this.store,
      this.store.select(fromStore.getExtractList),
      this.store.select(fromStore.getExtractListTotalCount),
      this.store.select(fromStore.getExtractListLoading),
      (p, s, search) => new extractsActions.GetExtractListAction(ModuleConfiguration.moduleName,
        ExtractListModel,
        {
          page: p.pageIndex,
          pageSize: p.pageSize,
          searchTerm: search.searchControl.value,
          model: this.selectedExtract
        }),
      this.sort,
      this.search
    );

    this.extractListLoading$ = this.store.select(fromStore.getExtractListLoading);
    this.extractList$ = this.store.select(fromStore.getExtractList);
    this.extractCategorySelected$ = this.store.select(fromStore.getExtractCategorySelected);

    this.actions$.ofType(extractsActions.FINALIZE_EXTRACT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: extractsActions.FinalizeExtractSuccessAction) => {
        this.refresh();
      });

    this.actions$.ofType(extractsActions.DELETE_EXTRACT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: extractsActions.DeleteExtractSuccessAction) => {
        this.refresh();
      });

    this.actions$.ofType(extractsActions.GENERATE_EXTRACT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: extractsActions.GenerateExtractSuccessAction) => {
        this.isReadyToGenerate = true;
        this.refresh();
        if (a.payload.extractKey != null){
          this.store.dispatch(new extractsActions.GenerateChildExtractsAction({ extractKey: a.payload.extractKey, model: this.selectedExtract }));
        }
      });

    this.actions$.ofType(ACTION_ERROR)
      .takeUntil(this.destroyed$)
      .subscribe((a: ActionError) => {
        this.isReadyToGenerate = true;
        a.payload.errors.forEach(e => this.alerts.addError(e));
      });
  }

  refresh(): void {
    this.store.dispatch(new extractsActions.GetExtractListAction(
      ModuleConfiguration.moduleName,
      ExtractListModel,
      {
        page: this.paginator == null ? 0 : this.paginator.pageIndex,
        pageSize: this.paginator == null ? 20 : this.paginator.pageSize,
        searchTerm: this.search == null ? null : this.search.searchControl.value,
        model: this.selectedExtract
      }));
  }
  
  finalize(extract: any): void {
    this.store.dispatch(new extractsActions.FinalizeExtractAction({ extractKey: extract.key, model: this.selectedExtract }));
  }

  delete(extract: any): void {
    this.store.dispatch(new extractsActions.DeleteExtractAction({ extractKey: extract.key, model: this.selectedExtract }));
  }

  downloadFile(extract: ExtractListModel, option: OptionModel<any>): void {
    var url = option.url + '/' + extract.key + '/?XAuthToken=' + this.curentUserService.userContext.authToken;
    window.open(this.baseAddressInterceptor.getApiUrl(url));
  }

  checkSelected() {
    var searchInput = sessionStorage.getItem(this.searchInput);
    if (searchInput) {
      this.search.searchControl.setValue(searchInput);
    }
  }

  generateExtract() {
    var extractPageViewModel = new ExtractPageViewModel();
    extractPageViewModel.page = 0;
    extractPageViewModel.pageSize = 10000;
    if (this.selectedExtract != null) {
      extractPageViewModel.model = this.selectedExtract;
    }
    this.store.dispatch(new extractsActions.GenerateExtractAction({ model: extractPageViewModel }));
    this.isReadyToGenerate = false;
  }

  navigate (extract: ExtractListModel, option: OptionModel<any>): void {
    if (option.url != null && option.url !== "") {
      this.store.dispatch(new extractsActions.SetSelectedExtractListModelAction({ model: extract }));
      this.saveSessionStorage(extract);
      this.router.navigateByUrl(option.url + extract.key);
    }
  }

  getOptionsByType(options: OptionModel<any>[], type: string): OptionModel<any>[] {
    return options.filter(o => o.type === type);
  }

  saveSessionStorage(extract: ExtractListModel) {
    sessionStorage.setItem(this.searchInput, this.search.searchControl.value);
    sessionStorage.setItem(this.selectedExtractCategory_ExtractModel, JSON.stringify(this.selectedExtract));
    sessionStorage.setItem(this.selectedExtractList_ExtractListModel, JSON.stringify(extract) );
  }
}



// WEBPACK FOOTER //
// ./src/app/extracts/components/extract-viewer.component.ts